import { isRef, ref, Ref, watch } from "@vue/composition-api";

interface committableParams<T> {
    originalValue: T | Ref<T>;
    watchOriginal: boolean;
    onCommit?: (entry: T) => Promise<T | void>;
}

export const useCommittable = <T extends any>({ originalValue, onCommit }: committableParams<T>) => {
    // @ts-ignore revert() will immediately populate this
    const clone: Ref<T> = ref<T>();
    const isDirty = ref(false);

    // Todo: if watch, revert() on change

    // Commit changes in the clone
    const commit = async () => {
        let commitResult;

        if (onCommit) {
            // Take the resulting value of onCommit and use that to replace the original with
            commitResult = await onCommit(clone.value);
            if (!commitResult) return;
        }

        if (isRef(originalValue)) {
            // Original is a ref. Set the value
            originalValue.value = Object.assign({}, commitResult || clone.value);
        } else {
            // Original is not a ref. Assign properties directly
            Object.assign(originalValue, commitResult || clone.value);
        }

        // Re-initialize the copy to contain the latest values
        revert(true);
    };

    // Undo changes in the clone, reset back to current original value
    const revert = (skipConfirm: boolean = false) => {
        if (!skipConfirm && !confirm("Are you sure you want to undo all changes?")) return;
        clone.value = Object.assign({}, isRef(originalValue) ? originalValue.value : originalValue);
        setTimeout(() => {
            // In a setTimeout because the watcher will trigger first
            isDirty.value = false;
        });
    };

    watch(
        clone,
        () => {
            isDirty.value = true;
        },
        {
            deep: true,
        },
    );

    // Initialize the clone
    revert(true);

    return { clone, revert, commit, isDirty };
};
